// FIRST-TIME MANAGER TRANSITION ASSESSMENT — Scoring Engine
// Scale: 0-4 per item (reverse-scored items are flipped before aggregation)
// Composite: Transition Readiness Index (TRI), 0-100
import { FTM_ITEMS, FTM_DIMS } from "./questionBank.js";

export const FTM_NORMS = {
  overall: { mean:58.6, sd:16.4, n:640 },
};

export const FTM_BANDS = [
  { min:0,  max:40, label:"High Transition Risk", level:"high_risk",
    color:"#dc2626", bg:"#fef2f2", border:"#fecaca", icon:"🔴",
    hire:"Significant risk of first-time manager failure without structured support. Immediate coaching and a formal onboarding plan strongly recommended before or immediately upon taking the role.",
    desc:"Gentry et al. (2010) found that first-time managers scoring in this range on transition-readiness measures show markedly higher rates of derailment in their first 18 months, typically driven by unresolved identity transition (still 'doing' rather than enabling) and avoidance of the interpersonal discomfort inherent in the role." },
  { min:40, max:60, label:"Developing Readiness", level:"developing",
    color:"#d97706", bg:"#fffbeb", border:"#fde68a", icon:"🟡",
    hire:"Typical profile for a first-time manager in the early months. Benefits significantly from structured coaching on specific gap areas identified below.",
    desc:"Most first-time managers land in this band during their first 6 months. This is not a red flag on its own, but the specific dimension gaps below indicate exactly where structured support will have the highest leverage." },
  { min:60, max:80, label:"Transition Ready", level:"ready",
    color:"#059669", bg:"#f0fdf4", border:"#86efac", icon:"🟢",
    hire:"Well-prepared for the demands of a first management role. Likely to navigate the identity shift with lighter-touch support.",
    desc:"Demonstrates the behavioural and psychological readiness associated with successful first-time manager transitions in Watkins' (2003) and Hill's (2003) research, particularly around delegation and letting go of individual-contributor identity." },
  { min:80, max:101, label:"Highly Ready", level:"highly_ready",
    color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe", icon:"🔵",
    hire:"Exceptionally well-positioned for the transition. Consider as an internal case study or peer-coach for other first-time managers.",
    desc:"Top-quartile transition readiness. This profile combines low reliance on personal execution, high delegation comfort, and demonstrated ease in the interpersonal demands unique to newly managing others." },
];

export const FTM_DIM_THRESHOLDS = {
  LG: { low:8, moderate:13, high:17 },
  DC: { low:8, moderate:13, high:17 },
  MP: { low:8, moderate:13, high:17 },
  FB: { low:8, moderate:13, high:17 },
  ST: { low:8, moderate:13, high:17 },
  BA: { low:8, moderate:13, high:17 },
};

export const FTM_RISK_FLAGS = [
  { id:"still_doing", dims:["LG"], threshold:"low",
    label:"Still Operating as an Individual Contributor",
    desc:"Low Letting-Go scores indicate the candidate is likely still deriving identity and satisfaction from personal execution rather than enabling others - Hill's (2003) single most cited cause of first-time manager burnout and team under-development.",
    action:"Coach explicitly on the identity shift: success is now defined by team output, not personal output. Use a 'stop doing' list exercise in the first 30 days." },
  { id:"micromanage", dims:["DC"], threshold:"low",
    label:"Delegation Avoidance Risk",
    desc:"Low Delegation Confidence combined with high personal-execution tendency predicts micromanagement - the single most common complaint direct reports raise about new managers in engagement survey verbatims.",
    action:"Practice delegating one complete, meaningful project within the first 2 weeks, with explicit checkpoints agreed in advance rather than ad hoc check-ins." },
  { id:"peer_conflict_avoidance", dims:["MP","FB"], threshold:"low",
    label:"Peer Relationship & Conflict Avoidance",
    desc:"Low scores on both Managing Former Peers and Difficult Feedback together predict a specific failure pattern: avoiding necessary accountability conversations with former colleagues, which erodes team performance and the new manager's credibility simultaneously.",
    action:"Have an explicit, direct conversation with each former peer about the changed relationship within the first month. Rehearse one specific difficult conversation with a coach before it becomes urgent." },
  { id:"reactive_only", dims:["ST"], threshold:"low",
    label:"Purely Reactive Time Allocation",
    desc:"Low Strategic Time Use suggests the candidate's calendar is likely dominated by tactical firefighting with no protected time for coaching or planning - a pattern Watkins (2003) identifies as self-perpetuating, since undeveloped teams generate more firefighting.",
    action:"Block two fixed weekly slots - one for 1:1 coaching conversations, one for forward planning - and protect them as non-negotiable for the first 90 days." },
  { id:"authority_deficit", dims:["BA"], threshold:"low",
    label:"Authority & Confidence Deficit",
    desc:"Low Managerial Authority scores predict hesitant, over-consultative decision-making that can read to a team as a lack of direction, even when the underlying judgement is sound.",
    action:"Identify 2-3 decision types the new manager can make unilaterally without escalation, and explicitly communicate this scope to the team to build visible authority quickly." },
];

export function scoreFTM(answers) {
  const dimScores = { LG:0, DC:0, MP:0, FB:0, ST:0, BA:0, SDE:0, IM:0 };
  const dimCounts = { LG:0, DC:0, MP:0, FB:0, ST:0, BA:0, SDE:0, IM:0 };

  FTM_ITEMS.forEach((item, i) => {
    const raw = answers[i];
    if (raw === null || raw === undefined) return;
    const val = item.reverse ? (4 - Number(raw)) : Number(raw);
    dimScores[item.dim] += val;
    dimCounts[item.dim]++;
  });

  const maxPerDim = 20;
  const dimResults = {};
  let total = 0, maxTotal = 0;

  Object.keys(FTM_DIMS).forEach(dim => {
    const raw = dimScores[dim];
    const count = dimCounts[dim];
    const max = count * 4;
    const pct = max > 0 ? Math.round((raw / max) * 100) : 0;
    const score100 = Math.round((raw / maxPerDim) * 100);
    const thresh = FTM_DIM_THRESHOLDS[dim];
    const level = raw >= thresh.high ? "high" : raw >= thresh.moderate ? "moderate" : "low";
    dimResults[dim] = { raw, max, pct, score100, level, count };
    total += raw;
    maxTotal += (count * 4);
  });

  const totalPct = maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
  const band = FTM_BANDS.find(b => totalPct >= b.min && totalPct < b.max) || FTM_BANDS[0];

  const norm = FTM_NORMS.overall;
  const z = (total - norm.mean) / norm.sd;
  const percentile = Math.min(99, Math.max(1, Math.round(50 + z * 17)));

  const activeFlags = FTM_RISK_FLAGS.filter(flag =>
    flag.dims.every(d => dimResults[d] && dimResults[d].level === flag.threshold)
  );

  // Response Validity — two-factor social-desirability check, same BIDR-
  // inspired structure used on ASSESS. SDE items score directly (agreeing
  // with an unrealistic claim = high self-deceptive enhancement); IM items
  // are reverse-scored (candidly admitting a common imperfection = low
  // impression management, i.e. honest).
  const bandFor = pct => pct>=85?"VERY_HIGH":pct>=65?"HIGH":pct>=40?"MODERATE":"LOW";
  const sdePct = dimCounts.SDE>0 ? Math.round((dimScores.SDE/(dimCounts.SDE*4))*100) : 0;
  const imPct  = dimCounts.IM>0  ? Math.round((dimScores.IM/(dimCounts.IM*4))*100)  : 0;
  const positiveFactors = {
    factor1: { label:"Self-Deceptive Enhancement", pct:sdePct, band:bandFor(sdePct), raw:dimScores.SDE, max:dimCounts.SDE*4 },
    factor2: { label:"Impression Management", pct:imPct, band:bandFor(imPct), raw:dimScores.IM, max:dimCounts.IM*4 },
  };

  return {
    dimResults,
    total, maxTotal, totalPct,
    percentile, band,
    riskFlags: activeFlags,
    positiveFactors,
    scales: { ftm: totalPct },
    perfAvg: totalPct / 10,
    potAvg: totalPct / 10,
    perfBox: totalPct >= 60 ? 3 : totalPct >= 40 ? 2 : 1,
    potBox: totalPct >= 60 ? 3 : totalPct >= 40 ? 2 : 1,
  };
}
